import React, { useEffect } from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import DragAndDropImport from 'root/apps/ModuleCompareUI/components/DragAndDropImport';
import {
  ModuleCompareActionTypes,
  useModuleCompare,
} from 'root/apps/ModuleCompareUI/context/Context';
import {
  ByFileModuleDTO,
  CompareDataApiResponse,
  ModuleList,
} from 'root/apps/ModuleCompareUI/ModuleCompare.types';
import { mergeModuleLists } from 'root/apps/ModuleCompareUI/utils/dataUtils';
import { DangerAlert } from 'root/apps/MultiLocationSaleModal/DangerAlert';

const useFetchCurrentAccountModuleList = (accountId: string, enabled: boolean) => {
  const { dispatch } = useModuleCompare();
  const query = useQuery<ModuleList>({
    queryKey: ['currentAccountModuleList'],
    queryFn: () =>
      axios.get<CompareDataApiResponse>(`/staff/account/${accountId}/module/`).then((response) => {
        return response.data.data.moduleList;
      }),
    enabled,
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: ModuleCompareActionTypes.SET_CURRENT_ACCOUNT_MODULE_LIST,
        payload: query.data,
      });
    }
  }, [query.data, dispatch]);

  return {
    isFetching: query.isFetching,
    isError: query.isError,
  };
};

const LoadingBones = () => {
  return (
    <Flex flexDirection="column" maxWidth="800px">
      <Bone height="2.5rem" width="80%" style={{ marginBottom: '1.5rem' }} />
      {Array(10)
        .fill('bones')
        .map((_entry, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <Flex flexDirection="row" style={{ gap: '2rem' }} key={idx} my={3}>
            <Bone height="2.5rem" width="40%" />
            <Bone height="2.5rem" width="5%" />
            <Bone height="2.5rem" width="40%" />
          </Flex>
        ))}
    </Flex>
  );
};

export const CompareByFile: React.FC = () => {
  const {
    accountId,
    state: { activeMode, currentAccountModuleList },
    dispatch,
  } = useModuleCompare();

  const { isFetching, isError } = useFetchCurrentAccountModuleList(accountId, !!accountId);

  const [filename, setFilename] = React.useState('');
  const [accountName, setAccountName] = React.useState('');
  const [environment, setEnvironment] = React.useState('');

  useEffect(() => {
    if (activeMode !== 'byFile') {
      setFilename('');
      setAccountName('');
      setEnvironment('');
    }
  }, [activeMode]);

  const handleFileParsed = (data: ByFileModuleDTO) => {
    const { moduleList, accountID, accountName, environment, fileName } = data;

    setFilename(fileName);
    setAccountName(accountName || '');
    setEnvironment(environment || '');

    const secondAccountId = accountID;
    if (!secondAccountId) {
      window.alert('Cannot compare the same account');
      return;
    }

    dispatch({
      type: ModuleCompareActionTypes.SET_SECOND_ACCOUNT_ID,
      payload: secondAccountId,
    });

    const mergedData = mergeModuleLists(
      currentAccountModuleList || {},
      moduleList || {},
      accountId,
      secondAccountId
    );
    dispatch({
      type: ModuleCompareActionTypes.SET_COMPARED_MODULES,
      payload: mergedData,
    });

    dispatch({
      type: ModuleCompareActionTypes.SET_ACTIVE_MODE,
      payload: 'byFile',
    });
  };

  if (isFetching) return <LoadingBones />;
  if (!isFetching && isError)
    return (
      <DangerAlert
        title="Something went wrong"
        message={`Account ${accountId} does not exist or there was an error fetching the data. Try again.`}
      />
    );

  return (
    <>
      <Text fontSize={18} fontWeight={600} pb={2} pt={4}>
        Compare Modules Across Enviroments
      </Text>
      <Text fontSize={16} fontWeight={500}>
        To compare modules between different environments, first export the modules from one
        environment.
      </Text>
      <Text fontSize={16} fontWeight={500}>
        Then, import the exported frile in another environment to run a comparison
      </Text>
      <Flex flexDirection="column" width="300px" mt="10px">
        <Flex flexDirection="row" alignItems="end">
          <Box>
            <Text fontSize={16} fontWeight={700}>
              Import modules from another account
            </Text>
            <DragAndDropImport onFileParsed={handleFileParsed} />
          </Box>
          <Box ml={4}>
            <Text fontSize={16}>
              {filename && (
                <>
                  <Text fontWeight={700}>File uploaded:</Text> {filename}
                </>
              )}
              {accountName && (
                <>
                  <Text fontWeight={700}>Account Name:</Text> {accountName}
                </>
              )}
              {environment && (
                <>
                  <Text fontWeight={700}>Environment:</Text> {environment}
                </>
              )}
            </Text>
          </Box>
        </Flex>
        <Text fontSize={16} fontWeight={500} mt={2}>
          OR
        </Text>
        <Button
          variant="default"
          onClick={() =>
            window.open(
              `/staff/account/${accountId}/module?download=true`,
              '_blank', // Opens in a new tab
              'noopener,noreferrer' // Security best practices
            )
          }
          fill
          mt={2}
        >
          Export modules from this account -{accountId}
        </Button>
      </Flex>
      <Flex flexDirection="column" width="300px" mt="10px" />
    </>
  );
};
